import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import * as XLSX from 'xlsx';
import {
  EmailLog,
  EmailLogFilterDto,
  EmailLogListResponse,
  EmailLogMetrics,
  BulkRetryResponse,
} from '@/types/email-log.types';

export interface ExportEmailLogsParams {
  status?: string;
  email_type?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
}

export interface EmailTypeOption {
  label: string;
  value: string;
}

class EmailLogService {
  /**
   * Get email type options for filter dropdown
   */
  async getEmailTypeOptions(): Promise<EmailTypeOption[]> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.EMAIL_LOGS.FILTER_OPTIONS);

      const data = response.data?.data || response.data;

      // If data is an array, return it directly
      if (Array.isArray(data)) {
        return data;
      }

      // If data has email_types property
      if (data?.email_types && Array.isArray(data.email_types)) {
        return data.email_types;
      }

      // If data has options property
      if (data?.options && Array.isArray(data.options)) {
        return data.options;
      }

      return [];
    } catch (error: any) {
      console.error('Error fetching email type options:', error);
      return [];
    }
  }

  async getEmailLogs(filters: EmailLogFilterDto = {}): Promise<EmailLogListResponse> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.EMAIL_LOGS.LIST, filters);

      if (response.data?.data) {
        return response.data.data;
      }

      return {
        logs: response.data?.logs || [],
        total: response.data?.total || 0,
        page: response.data?.page || 1,
        limit: response.data?.limit || 20,
        total_pages: response.data?.total_pages || 1,
      };
    } catch (error: any) {
      console.error('Error fetching email logs:', error);
      throw new Error(error?.response?.data?.description || 'Failed to fetch email logs');
    }
  }

  async getEmailLogById(id: string): Promise<EmailLog> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.EMAIL_LOGS.GET_BY_ID(id));

      if (response.data?.data) {
        return response.data.data;
      }

      return response.data;
    } catch (error: any) {
      console.error('Error fetching email log:', error);
      throw new Error(error?.response?.data?.description || 'Failed to fetch email log');
    }
  }

  async getMetrics(from?: string, to?: string, includeArchived?: boolean): Promise<EmailLogMetrics> {
    try {
      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      if (includeArchived) params.append('include_archived', 'true');

      const queryString = params.toString();
      const url = queryString
        ? `${API_ENDPOINTS.EMAIL_LOGS.METRICS}?${queryString}`
        : API_ENDPOINTS.EMAIL_LOGS.METRICS;

      const response = await apiClient.get(url);

      if (response.data?.data) {
        return response.data.data;
      }

      return response.data;
    } catch (error: any) {
      console.error('Error fetching email metrics:', error);
      throw new Error(error?.response?.data?.description || 'Failed to fetch email metrics');
    }
  }

  async retryEmail(id: string): Promise<EmailLog> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.EMAIL_LOGS.RETRY(id));
      const body = response.data;

      // The API returns HTTP 200 even on failures, with { success:false, code, error:{...} }.
      // Detect that and surface the backend's message (e.g. "Maximum retry attempts (3) reached").
      if (body && body.success === false) {
        throw new Error(
          body.error?.error_description || body.error?.error || body.description || 'Failed to retry email'
        );
      }

      if (body?.data) {
        return body.data;
      }
      return body;
    } catch (error: any) {
      console.error('Error retrying email:', error);
      // Handle both the thrown Error above and a real axios HTTP error.
      const message =
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.description ||
        error?.message ||
        'Failed to retry email';
      throw new Error(message);
    }
  }

  async bulkRetry(logIds: string[]): Promise<BulkRetryResponse> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.EMAIL_LOGS.BULK_RETRY, {
        log_ids: logIds,
      });

      if (response.data?.data) {
        return response.data.data;
      }

      return response.data;
    } catch (error: any) {
      console.error('Error bulk retrying emails:', error);
      throw new Error(error?.response?.data?.description || 'Failed to bulk retry emails');
    }
  }

  /**
   * Format date to readable string
   */
  private formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  }

  /**
   * Get status label
   */
  private getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      pending: 'Pending',
      sent: 'Sent',
      delivered: 'Delivered',
      failed: 'Failed',
      retry_scheduled: 'Retry Scheduled',
      bounced: 'Bounced',
    };
    return labels[status] || status;
  }

  /**
   * Get email type label
   */
  private getEmailTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      welcome: 'Welcome Email',
      password_reset: 'Password Reset',
      invitation: 'Invitation',
      reminder: 'Reminder',
      notification: 'Notification',
      certificate: 'Certificate',
      other: 'Other',
    };
    return labels[type] || type;
  }

  /**
   * Generate CSV content from email logs
   */
  private generateEmailLogsCSV(logs: EmailLog[]): string {
    const headers = [
      'Recipient Email',
      'Recipient Name',
      'Subject',
      'Email Type',
      'Status',
      'Created At',
      'Sent At',
      'Failed At',
      'Error Message',
      'Error Code',
      'Retry Count',
      'Template Name',
      'Log ID'
    ];

    const rows = logs.map(log => [
      log.recipient_email || '',
      log.recipient_name || '',
      log.subject || '',
      this.getEmailTypeLabel(log.email_type),
      this.getStatusLabel(log.status),
      this.formatDate(log.created_at),
      this.formatDate(log.sent_at),
      this.formatDate(log.failed_at),
      log.error_message || '',
      log.error_code || '',
      log.retry_count !== undefined ? `${log.retry_count}/${log.max_retries || 0}` : 'N/A',
      log.template_name || '',
      log._id
    ]);

    return [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
  }

  /**
   * Generate Excel file from email logs
   */
  private generateEmailLogsExcel(logs: EmailLog[]): Blob {
    const headers = [
      'Recipient Email',
      'Recipient Name',
      'Subject',
      'Email Type',
      'Status',
      'Created At',
      'Sent At',
      'Failed At',
      'Error Message',
      'Error Code',
      'Retry Count',
      'Template Name',
      'Log ID'
    ];

    const rows = logs.map(log => [
      log.recipient_email || '',
      log.recipient_name || '',
      log.subject || '',
      this.getEmailTypeLabel(log.email_type),
      this.getStatusLabel(log.status),
      this.formatDate(log.created_at),
      this.formatDate(log.sent_at),
      this.formatDate(log.failed_at),
      log.error_message || '',
      log.error_code || '',
      log.retry_count !== undefined ? `${log.retry_count}/${log.max_retries || 0}` : 'N/A',
      log.template_name || '',
      log._id
    ]);

    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths
    worksheet['!cols'] = [
      { wch: 30 }, // Recipient Email
      { wch: 25 }, // Recipient Name
      { wch: 40 }, // Subject
      { wch: 15 }, // Email Type
      { wch: 15 }, // Status
      { wch: 20 }, // Created At
      { wch: 20 }, // Sent At
      { wch: 20 }, // Failed At
      { wch: 40 }, // Error Message
      { wch: 15 }, // Error Code
      { wch: 12 }, // Retry Count
      { wch: 25 }, // Template Name
      { wch: 25 }, // Log ID
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Email Logs');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  }

  /**
   * Download CSV content as file
   */
  private downloadCSV(csvContent: string, filename: string): void {
    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;

    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Download Excel blob as file
   */
  private downloadExcel(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Generate filename for export
   */
  private generateExportFilename(format: 'csv' | 'xlsx', filters: ExportEmailLogsParams): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const parts = ['email-logs'];

    if (filters.status) {
      parts.push(`status-${filters.status}`);
    }

    if (filters.email_type) {
      parts.push(`type-${filters.email_type}`);
    }

    if (filters.search) {
      const sanitizedSearch = filters.search.replace(/[^a-z0-9]/gi, '-').toLowerCase().substring(0, 20);
      parts.push(`search-${sanitizedSearch}`);
    }

    parts.push(timestamp);

    return parts.join('_') + `.${format}`;
  }

  /**
   * Export email logs to CSV or Excel
   * Fetches all data (no pagination) and generates file on frontend
   */
  async exportLogs(
    filters: ExportEmailLogsParams,
    format: 'csv' | 'xlsx' = 'csv'
  ): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    try {
      // Fetch all logs without pagination limit
      const response = await apiClient.post(API_ENDPOINTS.EMAIL_LOGS.LIST, {
        ...filters,
        limit: 10000,
      });

      const logsData = response.data?.data?.logs || response.data?.logs || [];

      if (logsData.length === 0) {
        return {
          success: false,
          filename: '',
          count: 0,
          message: 'No email logs found to export with the current filters'
        };
      }

      const filename = this.generateExportFilename(format, filters);

      if (format === 'xlsx') {
        const excelBlob = this.generateEmailLogsExcel(logsData);
        this.downloadExcel(excelBlob, filename);
      } else {
        const csvContent = this.generateEmailLogsCSV(logsData);
        this.downloadCSV(csvContent, filename);
      }

      return {
        success: true,
        filename,
        count: logsData.length
      };
    } catch (error: any) {
      console.error('Error exporting email logs:', error);
      throw new Error(error?.response?.data?.description || 'Failed to export email logs');
    }
  }
}

export const emailLogService = new EmailLogService();
